import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "examples/LayoutContainers/PageLayout";
import { GetPaymentPlans } from "components/Payment/PaymentApi";
import {
  buildAuthRoute,
  persistCheckoutPlan,
} from "utils/checkoutFlow";
import { APP_DASHBOARD_ROUTE, SIGN_IN_ROUTE, SIGN_UP_ROUTE } from "utils/routes";
import "./landing.css";
import { showCookiePreferences } from "components/CookieConsentManager";

const ANNUAL_PLAN_CONTACT_URL = "https://wa.me/351910561912?text=Quero%20travar%20o%20plano%20ProAnual%20do%20Patrim%C3%B4nio360";
const ANNUAL_LAUNCH_SPOTS_LABEL = "Condição fundadora para os primeiros 50 assinantes";

const trustItems = [
  {
    title: "Visão unificada",
    text: "Patrimônio, caixa, metas e orçamento no mesmo fluxo de decisão.",
  },
  {
    title: "Rotina financeira",
    text: "Receitas, despesas e limites por categoria deixam o mês mais previsível.",
  },
  {
    title: "Metas no radar",
    text: "Acompanhe o que já foi guardado e quanto falta para cada objetivo.",
  },
  {
    title: "Ação guiada",
    text: "Do orçamento ao rebalanceamento, saiba com mais clareza qual ajuste vem agora.",
  },
];

const comparisonCards = [
  {
    title: "Sem o Patrimônio360",
    items: [
      "Dados espalhados entre planilhas, banco, corretora e anotações.",
      "Dificuldade para saber se o mês está dentro do orçamento e quanto ainda pode gastar.",
      "Metas financeiras sem acompanhamento claro do que já foi guardado.",
      "Carteira sai da estratégia enquanto o caixa continua no improviso.",
    ],
  },
  {
    title: "Com o Patrimônio360",
    items: [
      "Painel consolidado para patrimônio, receitas, despesas, orçamento e metas.",
      "Resumo do mês com planejado x realizado, projeção e limites por categoria.",
      "Metas acompanhadas com progresso claro entre valor alvo e valor guardado.",
      "Mais critério para aportar, rebalancear e evitar decisões no improviso.",
    ],
  },
];

const featureCards = [
  {
    pill: "Dashboard patrimonial",
    title: "Veja patrimônio, caixa e saúde financeira sem trocar de tela.",
    text: "Comece pela visão geral. O painel reúne patrimônio total, caixa, saúde financeira e carteira atual para que você entenda rapidamente sua posição e os sinais que merecem atenção.",
    points: [
      "Resumo patrimonial e financeiro em um único painel.",
      "Ativos cadastrados com leitura mais clara de concentração e participação.",
      "Melhor percepção de onde está o risco e onde está o progresso.",
    ],
    image: "/landing/site1.jpeg",
    alt: "Tela do dashboard patrimonial",
  },
  {
    pill: "Receita x despesa",
    title: "Registre entradas e saídas com contexto para decidir melhor o mês.",
    text: "O fluxo de caixa deixa de ser detalhe. Receitas e despesas passam a mostrar saldo, tendência e espaço real para poupar, aportar ou segurar gastos.",
    points: [
      "Controle simples de receitas e despesas.",
      "Extrato consolidado para acompanhar tendência e saldo.",
      "Mais clareza sobre o que realmente sobra para investir.",
      "Disponível desde o plano grátis.",
    ],
    image: "/landing/site2.jpeg",
    alt: "Tela de receitas e despesas",
    reverse: true,
  },
  {
    pill: "Orçamento do mês",
    title: "Transforme o mês em um plano com projeção e alertas práticos.",
    text: "O orçamento cruza planejado e realizado, mostra saldo previsto, livre para gastar e desvio projetado para que o mês não seja acompanhado só no fim.",
    points: [
      "Receita prevista, despesa planejada e saldo em poucos segundos.",
      "Projeção inteligente do mês com comparação entre planejado e realizado.",
      "Limites por categoria para enxergar onde o orçamento apertou.",
      "Disponível desde o plano grátis.",
    ],
    image: "/landing/site1.jpeg",
    alt: "Tela de orçamento mensal",
  },
  {
    pill: "Metas e objetivos",
    title: "Acompanhe cada objetivo com progresso claro ao longo do mês.",
    text: "Cadastre metas, defina valor alvo, registre quanto já foi guardado e acompanhe a evolução sem depender de planilha ou memória.",
    points: [
      "Metas mensais organizadas em um único lugar.",
      "Progresso claro entre valor alvo e guardado.",
      "Mais disciplina para poupar com intenção.",
      "Disponível desde o plano grátis.",
    ],
    image: "/landing/site2.jpeg",
    alt: "Tela de metas financeiras",
    reverse: true,
  },
  {
    pill: "Rebalanceamento",
    title: "Compare exposição atual e meta sem achismos.",
    text: "Quando o caixa e as metas estão organizados, fica mais fácil usar o rebalanceamento para ajustar a carteira com critério.",
    points: [
      "Meta de exposição lado a lado com a carteira real.",
      "Mais clareza para reduzir concentração e corrigir desvios.",
      "Recurso essencial do plano Pro.",
    ],
    image: "/landing/rebalancear.gif",
    alt: "Funcionalidade de controle de exposição e rebalanceamento",
  },
  {
    pill: "Simulador de alocação",
    title: "Transforme capital disponível em ajuste objetivo de exposição.",
    text: "O simulador conecta o valor disponível à estratégia. Em vez de decidir no impulso, você informa o montante livre e recebe uma distribuição mais coerente com sua meta.",
    points: [
      "Uso simples e direto para redistribuir capital com frequência.",
      "Ajuda a reduzir viés, concentração e decisões no achismo.",
      "Mais um recurso forte do plano Pro.",
    ],
    image: "/landing/calculadora.gif",
    alt: "Simulador de alocação",
    reverse: true,
  },
];

const steps = [
  {
    number: "01",
    title: "Centralize sua base",
    text: "Reúna ativos, receitas, despesas, dividendos e saldo do mês em um fluxo só.",
  },
  {
    number: "02",
    title: "Planeje o mês",
    text: "Defina orçamento, acompanhe o planejado x realizado e configure limites por categoria.",
  },
  {
    number: "03",
    title: "Acompanhe metas",
    text: "Veja quanto já foi guardado, o que falta para cada objetivo e como isso impacta o caixa.",
  },
  {
    number: "04",
    title: "Decida aportes e ajustes",
    text: "Use exposição, rebalanceamento e simulador para escolher o próximo movimento com mais critério.",
  },
];

const faqs = [
  {
    question: "Para quem o Patrimônio360 faz mais sentido hoje?",
    answer: "Para quem investe, organiza a vida financeira e quer acompanhar patrimônio, caixa, metas, orçamento e carteira sem depender de várias planilhas.",
  },
  {
    question: "Consigo começar sem pagar?",
    answer: "Sim. O plano gratuito ajuda a organizar a base, registrar receitas e despesas, montar orçamento, acompanhar metas e sentir o valor do produto antes de assinar qualquer plano pago.",
  },
  {
    question: "O que realmente muda no Pro?",
    answer: "Metas, orçamento e fluxo de caixa já entram no grátis. O Pro libera a camada mais forte de decisão sobre a carteira: rebalanceamento, simulador de alocação, controle mais avançado de exposição e mais capacidade para acompanhar a base sem limites.",
  },
  {
    question: "Vale pegar o anual logo de cara?",
    answer: "Na maioria dos casos, faz mais sentido começar no grátis ou no Pro Mensal. Quando o Patrimônio360 já estiver encaixado na sua rotina de caixa, metas e investimentos, o anual vira a melhor escolha para reduzir custo e manter a disciplina por mais tempo. A oferta de lançamento existe para acelerar essa decisão sem empurrar um compromisso cedo demais.",
  },
  {
    question: "Posso falar com alguém antes de assinar?",
    answer: "Sim. Se quiser entender melhor o encaixe para o seu perfil, você pode falar diretamente com a equipe pelo WhatsApp.",
  },
];

const fallbackCatalogPlans = [
  {
    tierId: 1,
    slug: "gratis",
    name: "Grátis",
    priceInCents: 0,
    formattedPrice: "R$ 0,00",
    billingLabel: "Acesso gratuito",
    checkoutEnabled: false,
  },
  {
    tierId: 2,
    slug: "standard",
    name: "Standard",
    priceInCents: 1990,
    formattedPrice: "R$ 19,90",
    billingLabel: "30 dias de acesso",
    checkoutEnabled: true,
  },
  {
    tierId: 3,
    slug: "pro",
    name: "Pro",
    priceInCents: 2990,
    formattedPrice: "R$ 29,90",
    billingLabel: "30 dias de acesso",
    checkoutEnabled: true,
    highlight: true,
  },
  {
    tierId: 4,
    slug: "proanual",
    name: "ProAnual",
    priceInCents: 37880,
    formattedPrice: "R$ 378,80",
    billingLabel: "12 meses de acesso",
    checkoutEnabled: true,
  },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 2,
  }).format(value);

const formatCurrencyFromCents = (valueInCents = 0) => formatCurrency((valueInCents || 0) / 100);

const deriveFounderMonthlyEquivalentInCents = (monthlyPriceInCents) =>
  Math.floor(((monthlyPriceInCents || 0) * 0.8) / 10) * 10;

const deriveAnnualPriceInCents = (monthlyPriceInCents) =>
  deriveFounderMonthlyEquivalentInCents(monthlyPriceInCents) * 12;

const buildMarketingPlans = (catalogPlans = []) => {
  const plans = Array.isArray(catalogPlans) && catalogPlans.length > 0 ? catalogPlans : fallbackCatalogPlans;

  const freePlan = plans.find((plan) => !plan.checkoutEnabled || plan.slug === "gratis") || fallbackCatalogPlans[0];
  const annualCandidate =
    plans.find((plan) => /anual|annual|year/i.test(`${plan.slug || ""} ${plan.name || ""}`)) || null;

  const monthlyCandidate =
    plans.find((plan) => String(plan.slug).toLowerCase() === "pro") ||
    plans.find((plan) => plan.highlight && plan.checkoutEnabled) ||
    [...plans]
      .filter((plan) => plan.checkoutEnabled)
      .sort((leftPlan, rightPlan) => (rightPlan.priceInCents || 0) - (leftPlan.priceInCents || 0))[0] ||
    fallbackCatalogPlans[2];

  const monthlyPriceInCents = monthlyCandidate?.priceInCents ?? fallbackCatalogPlans[2].priceInCents;
  const regularAnnualReferenceInCents = monthlyPriceInCents * 12;
  const derivedAnnualPriceInCents = annualCandidate?.priceInCents ?? deriveAnnualPriceInCents(monthlyPriceInCents);
  const annualSavingsInCents = Math.max(regularAnnualReferenceInCents - derivedAnnualPriceInCents, 0);
  const annualMonthlyEquivalent = derivedAnnualPriceInCents / 12 / 100;

  return [
    {
      tierId: freePlan.tierId,
      slug: freePlan.slug || "gratis",
      name: "Grátis",
      kicker: "Entrada sem risco",
      formattedPrice: freePlan.formattedPrice || "R$ 0,00",
      billingText: "para provar valor",
      priceNote: "Importe sua posição, organize a base financeira e veja se o produto entra na sua rotina.",
      description:
        "Ideal para sair da desorganização, consolidar patrimônio, controlar o mês e acompanhar metas antes de assumir um compromisso pago.",
      features: [
        "Importação manual da planilha da B3.",
        "Visão inicial de patrimônio, caixa e saldo do mês.",
        "Receitas, despesas, orçamento e metas já liberados no grátis.",
        "Upgrade só quando fizer sentido remover limites e destravar a camada Pro.",
      ],
      ctaLabel: "Criar conta grátis",
      checkoutEnabled: false,
      highlight: false,
    },
    {
      tierId: monthlyCandidate.tierId,
      slug: monthlyCandidate.slug || "pro",
      name: "Pro Mensal",
      kicker: "Sem fidelidade",
      formattedPrice: monthlyCandidate.formattedPrice || formatCurrencyFromCents(monthlyPriceInCents),
      billingText: "/mês",
      priceNote: "Para quem já usa a base grátis e quer remover limites ou destravar a camada Pro sem travar 12 meses.",
      description:
        "A melhor opção para quem já organizou patrimônio, metas e orçamento no grátis e agora quer mais capacidade e ferramentas avançadas de alocação.",
      features: [
        "Tudo do plano grátis.",
        "Ativos e transações sem limite.",
        "Controle de exposição por tipo e por ativo.",
        "Rebalanceamento da carteira e simulador de alocação.",
      ],
      ctaLabel: "Começar com Pro Mensal",
      checkoutEnabled: Boolean(monthlyCandidate.checkoutEnabled),
      highlight: false,
    },
    {
      tierId: annualCandidate?.tierId || 4,
      slug: annualCandidate?.slug || "proanual",
      name: annualCandidate?.name || "ProAnual",
      kicker: "Preço fundador de lançamento",
      formattedPrice: annualCandidate?.formattedPrice || formatCurrencyFromCents(derivedAnnualPriceInCents),
      billingText: "/ano",
      previousFormattedPrice: formatCurrencyFromCents(regularAnnualReferenceInCents),
      priceNote: `Equivale a ${formatCurrency(annualMonthlyEquivalent)}/mês durante 12 meses.`,
      helperText: `Economize ${formatCurrencyFromCents(annualSavingsInCents)} por ano em relação ao Pro Mensal.`,
      urgencyText: ANNUAL_LAUNCH_SPOTS_LABEL,
      description:
        "Para quem já incorporou o Patrimônio360 à rotina e quer manter a camada Pro de alocação ativa com menos custo e mais consistência ao longo do ano.",
      features: [
        "Tudo do plano grátis e do Pro Mensal por 12 meses.",
        "Menor custo para manter rebalanceamento e simulador ativos.",
        "Mais chance de consolidar disciplina financeira e de investimento.",
        annualCandidate?.checkoutEnabled
          ? "Melhor opção para quem já quer entrar no longo prazo."
          : "Pode ser ativado com apoio da equipe enquanto o checkout anual entra no ar.",
      ],
      ctaLabel: annualCandidate?.ctaLabel || "Quero o preço fundador",
      checkoutEnabled: Boolean(annualCandidate?.checkoutEnabled),
      externalUrl: annualCandidate?.checkoutEnabled ? null : ANNUAL_PLAN_CONTACT_URL,
      badgeText: "Oferta de lançamento",
      highlight: true,
    },
  ];
};

function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [plans, setPlans] = useState(() => buildMarketingPlans(fallbackCatalogPlans));
  const [plansError, setPlansError] = useState(null);

  const hasAuthToken =
    typeof window !== "undefined" && Boolean(window.localStorage.getItem("tradez-token"));

  useEffect(() => {
    document.title = "Patrimônio360 | Patrimônio, caixa, metas, orçamento e alocação";

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Centralize patrimônio, fluxo de caixa, orçamento, metas e exposição por ativo para decidir melhor seus próximos passos com o Patrimônio360."
      );
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      try {
        const response = await GetPaymentPlans();

        if (!isMounted || !Array.isArray(response) || response.length === 0) {
          return;
        }

        setPlans(buildMarketingPlans(response));
        setPlansError(null);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setPlansError(error.message);
      }
    };

    loadPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth > 980) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleCloseMenu = () => setMenuOpen(false);

  const handlePlanAction = (plan) => {
    handleCloseMenu();

    if (plan?.externalUrl) {
      window.open(plan.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }

    persistCheckoutPlan(plan);

    if (!plan.checkoutEnabled) {
      navigate(hasAuthToken ? APP_DASHBOARD_ROUTE : SIGN_UP_ROUTE);
      return;
    }

    if (hasAuthToken) {
      navigate(APP_DASHBOARD_ROUTE);
      return;
    }

    navigate(buildAuthRoute(SIGN_UP_ROUTE, plan.slug));
  };

  const handlePrimaryCta = () => {
    handleCloseMenu();
    navigate(hasAuthToken ? APP_DASHBOARD_ROUTE : SIGN_UP_ROUTE);
  };

  return (
    <PageLayout background="default">
      <div className="landing-page">
        <header className="landing-site-header" data-menu-open={menuOpen}>
          <div className="landing-container landing-site-header-inner">
            <a className="landing-brand" href="#top" aria-label="Patrimônio360" onClick={handleCloseMenu}>
              <img src="/logoo-vector-light.svg" alt="Patrimônio360" />
            </a>

            <button
              className="landing-menu-toggle"
              type="button"
              aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={menuOpen}
              aria-controls="landing-site-nav"
              onClick={() => setMenuOpen((currentValue) => !currentValue)}
            >
              <span className="landing-menu-toggle-stack" aria-hidden="true">
                <span className="landing-menu-toggle-line" />
                <span className="landing-menu-toggle-line" />
                <span className="landing-menu-toggle-line" />
              </span>
            </button>

            <nav className="landing-site-nav" id="landing-site-nav" aria-label="Navegação principal">
              <a href="#recursos" onClick={handleCloseMenu}>Recursos</a>
              <a href="#como-funciona" onClick={handleCloseMenu}>Como funciona</a>
              <a href="#planos" onClick={handleCloseMenu}>Planos</a>
              <Link to={SIGN_IN_ROUTE} onClick={handleCloseMenu}>
                Entrar
              </Link>
              <button className="landing-button landing-button-primary" type="button" onClick={handlePrimaryCta}>
                {hasAuthToken ? "Ir para o painel" : "Criar conta grátis"}
              </button>
            </nav>
          </div>
        </header>

        <main id="top">
          <section className="landing-hero">
            <div className="landing-container landing-hero-grid">
              <div>
                <div className="landing-eyebrow">Patrimônio, caixa, metas e alocação em um só lugar</div>
                <h1>Organize patrimônio, acompanhe receitas e despesas, planeje o mês e invista com mais clareza.</h1>
                <p>
                  O Patrimônio360 reúne patrimônio, fluxo de caixa, orçamento mensal, metas financeiras
                  e estratégia de alocação em um fluxo simples. Em vez de navegar por planilhas e intuição,
                  você passa a enxergar o mês, a carteira e os próximos ajustes com mais consistência.
                </p>

                <div className="landing-hero-actions">
                  <button className="landing-button landing-button-primary" type="button" onClick={handlePrimaryCta}>
                    {hasAuthToken ? "Ir para o painel" : "Começar grátis"}
                  </button>
                  <a className="landing-button landing-button-secondary" href="#planos">
                    Ver planos
                  </a>
                  <a
                    className="landing-button landing-button-secondary"
                    href="https://wa.me/351910561912"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Falar no WhatsApp
                  </a>
                </div>

                <ul className="landing-hero-list">
                  <li>Tenha uma visão 360 do patrimônio, do caixa, do orçamento e da exposição em um painel só.</li>
                  <li>Registre receitas e despesas, projete o mês e configure limites por categoria.</li>
                  <li>Acompanhe metas e objetivos com clareza sobre quanto já foi guardado.</li>
                  <li>Use o capital disponível para corrigir concentração com mais critério.</li>
                </ul>

                <div className="landing-hero-proof">
                  Feito para quem quer unir organização financeira e investimento de longo prazo na mesma rotina.
                </div>
              </div>

              <div className="landing-hero-card">
                <img src="/landing/site1.jpeg" alt="Painel do Patrimônio360 com visão geral da carteira" />
                <div className="landing-floating-note">
                  <strong>Do caixa ao aporte</strong>
                  <p>
                    Veja patrimônio, orçamento, metas e concentração por ativo em poucos minutos.
                    O próximo passo deixa de ser improviso e vira decisão guiada.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="landing-trust-strip">
            <div className="landing-container landing-trust-grid">
              {trustItems.map((item) => (
                <article className="landing-trust-card" key={item.title}>
                  <span>{item.title}</span>
                  <strong>{item.text}</strong>
                </article>
              ))}
            </div>
          </section>

          <section className="landing-section landing-section-light">
            <div className="landing-container">
              <div className="landing-section-heading">
                <div className="landing-eyebrow">Por que escolher o Patrimônio360</div>
                <h2 className="landing-section-title">
                  Mais clareza para acompanhar patrimônio, caixa, metas e exposição com critério.
                </h2>
                <p className="landing-section-text">
                  O Patrimônio360 não serve apenas para registrar informações. Ele ajuda você a entender
                  o mês, acompanhar objetivos, enxergar a concentração da carteira e decidir o próximo ajuste com mais confiança.
                </p>
              </div>

              <div className="landing-compare-grid">
                {comparisonCards.map((card) => (
                  <article className="landing-compare-card" key={card.title}>
                    <h3>{card.title}</h3>
                    <ul>
                      {card.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="recursos" className="landing-section landing-section-soft">
            <div className="landing-container">
              <div className="landing-section-heading">
                <div className="landing-eyebrow">Recursos principais</div>
                <h2 className="landing-section-title">
                  Da rotina financeira do mês até a camada Pro de alocação.
                </h2>
                <p className="landing-section-text">
                  Cada recurso foi pensado para ajudar você a acompanhar patrimônio, planejar o caixa,
                  sustentar metas e tomar decisões de investimento com mais consistência.
                </p>
              </div>

              <div className="landing-feature-grid">
                {featureCards.map((feature) => (
                  <article
                    className={`landing-feature-card${feature.reverse ? " reverse" : ""}`}
                    key={feature.title}
                  >
                    <div className="landing-card-copy">
                      <div className="landing-feature-pill">{feature.pill}</div>
                      <h3>{feature.title}</h3>
                      <p>{feature.text}</p>
                      <ul className="landing-feature-points">
                        {feature.points.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="landing-feature-image">
                      <img src={feature.image} alt={feature.alt} loading="lazy" />
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="como-funciona" className="landing-section landing-section-dark">
            <div className="landing-container">
              <div className="landing-section-heading">
                <div className="landing-eyebrow">Como funciona</div>
                <h2 className="landing-section-title">
                  Um caminho simples entre organizar o mês e decidir melhor o próximo aporte.
                </h2>
              </div>

              <div className="landing-steps-grid">
                {steps.map((step) => (
                  <article className="landing-step-card" key={step.number}>
                    <div className="landing-step-number">{step.number}</div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="planos" className="landing-section landing-section-light">
            <div className="landing-container">
              <div className="landing-pricing-header">
                <div>
                  <div className="landing-eyebrow">Planos</div>
                  <h2 className="landing-pricing-title">
                    Comece grátis e avance quando fizer sentido.
                  </h2>
                  <p className="landing-section-text">
                    O grátis já cobre receitas, despesas, orçamento e metas. Os planos pagos entram
                    para remover limites e liberar a camada Pro de alocação para quem quiser dar esse próximo passo.
                  </p>
                </div>
                <button
                  className="landing-button landing-button-dark landing-pricing-header-cta"
                  type="button"
                  onClick={handlePrimaryCta}
                >
                  {hasAuthToken ? "Ir para o painel" : "Começar grátis"}
                </button>
              </div>

              {plansError && (
                <div className="landing-inline-feedback">
                {JSON.stringify(plansError)}  Não foi possível atualizar os planos da API agora. Exibindo a versão padrão da landing.
                </div>
              )}

              <div className="landing-pricing-grid">
                {plans.map((plan) => (
                  <article
                    className={`landing-pricing-card${plan.highlight ? " highlight" : ""}`}
                    key={plan.tierId}
                  >
                    {(plan.badgeText || plan.kicker) && (
                      <div className="landing-pricing-topline">
                        {plan.kicker && <div className="landing-pricing-kicker">{plan.kicker}</div>}
                        {plan.badgeText && <span className="landing-pricing-badge">{plan.badgeText}</span>}
                      </div>
                    )}
                    <h3>{plan.name}</h3>
                    {plan.previousFormattedPrice && (
                      <div className="landing-price-compare">
                        De <span>{plan.previousFormattedPrice}</span>
                      </div>
                    )}
                    <div className="landing-price">
                      <strong>{plan.formattedPrice}</strong>
                      <span>{plan.billingText}</span>
                    </div>
                    {plan.priceNote && <div className="landing-price-note">{plan.priceNote}</div>}
                    <p className="landing-pricing-copy">{plan.description}</p>
                    <ul className="landing-pricing-list">
                      {plan.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                    {plan.urgencyText && <div className="landing-pricing-urgency">{plan.urgencyText}</div>}
                    {plan.helperText && <div className="landing-pricing-helper">{plan.helperText}</div>}
                    <button
                      className={`landing-button ${plan.highlight ? "landing-button-primary" : "landing-button-dark"}`}
                      type="button"
                      onClick={() => handlePlanAction(plan)}
                    >
                      {plan.externalUrl
                        ? plan.ctaLabel
                        : plan.checkoutEnabled
                          ? hasAuthToken
                            ? `Continuar com ${plan.name}`
                            : plan.ctaLabel
                          : hasAuthToken
                            ? "Ir para o painel"
                            : plan.ctaLabel}
                    </button>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="landing-section landing-section-soft">
            <div className="landing-container">
              <div className="landing-section-heading">
                <div className="landing-eyebrow">Perguntas frequentes</div>
                <h2 className="landing-section-title">Tire as principais dúvidas antes que elas virem desistência.</h2>
              </div>

              <div className="landing-faq-grid">
                {faqs.map((faq) => (
                  <details className="landing-faq-item" key={faq.question}>
                    <summary>{faq.question}</summary>
                    <p>{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <section className="landing-section landing-section-light">
            <div className="landing-container">
              <div className="landing-cta-banner">
                <div>
                  <h2>Comece grátis agora e monte sua rotina de patrimônio, caixa e metas antes do upgrade.</h2>
                  <p>
                    Primeiro, organize a base, o orçamento e os objetivos no grátis. Depois, avance para
                    o Pro Mensal ou trave o ProAnual quando fizer sentido remover limites e ligar a camada Pro.
                  </p>
                </div>

                <div className="landing-cta-actions">
                  <button className="landing-button landing-button-primary" type="button" onClick={handlePrimaryCta}>
                    {hasAuthToken ? "Ir para o painel" : "Criar conta grátis"}
                  </button>
                  <a
                    className="landing-button landing-button-dark"
                    href="https://wa.me/351910561912"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Falar no WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer className="landing-footer">
          <div className="landing-container landing-footer-grid">
            <div className="landing-footer-brand">
              <img src="/logoo-vector-light.svg" alt="Patrimônio360" />
              <div className="landing-footer-meta">
                <div>Gestão patrimonial e financeira com foco em clareza, orçamento, metas e disciplina de risco.</div>
                <div>
                  Contato: <a href="mailto:info@patrimonio360.com">info@patrimonio360.com</a> |{" "}
                  <a href="tel:+351910561912">+351 910 561 912</a>
                </div>
                <div>&copy; {new Date().getFullYear()} Patrimônio360. Todos os direitos reservados.</div>
                <button className="landing-footer-link" type="button" onClick={showCookiePreferences}>
                  Preferências de cookies
                </button>
              </div>
            </div>

            <div className="landing-footer-actions">
              <a
                className="landing-social-link"
                href="https://www.linkedin.com/company/patrimonio360app/"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
              >
                <img src="/landing/linkedin.png" alt="" />
              </a>
              <a
                className="landing-social-link"
                href="https://www.instagram.com/patrimonio360plataforma/"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
              >
                <img src="/landing/instagram.png" alt="" />
              </a>
            </div>
          </div>
        </footer>

        <div className="landing-mobile-cta">
          <button className="landing-button landing-button-primary" type="button" onClick={handlePrimaryCta}>
            {hasAuthToken ? "Painel" : "Começar grátis"}
          </button>
          <a className="landing-button landing-button-secondary" href="#planos">
            Planos
          </a>
        </div>
      </div>
    </PageLayout>
  );
}

export default LandingPage;
